import { CartAddEvent, CartUpdateEvent } from '@theme/events';

const root = () => window.Shopify?.routes?.root ?? '/';

/**
 * Posts to a cart endpoint and announces the change so the cart drawer re-renders.
 * @param {string} path - cart/add.js or cart/change.js
 * @param {object} body
 * @param {HTMLElement} source
 * @param {'add' | 'update'} kind
 */
async function cartRequest(path, body, source, kind) {
  const response = await fetch(`${root()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.description || data.message || String(response.status));
  const cart = await (await fetch(`${root()}cart.js`)).json();
  const Event = kind === 'add' ? CartAddEvent : CartUpdateEvent;
  source.dispatchEvent(new Event({}, source.id || source.tagName.toLowerCase(), { source: source.tagName.toLowerCase(), itemCount: cart.item_count }));
  return cart;
}

/** Countdown "Cart reserved for mm:ss". Restarts when it reaches zero. */
class RowaCartTimer extends HTMLElement {
  #interval = 0;

  connectedCallback() {
    this.#tick();
    this.#interval = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#interval);
  }

  get #duration() {
    return Math.max(1, Number(this.dataset.minutes) || 10) * 60 * 1000;
  }

  #deadline() {
    const key = 'rowa-cart-timer';
    let end = 0;
    try {
      end = Number(sessionStorage.getItem(key)) || 0;
    } catch {}
    if (!end || end < Date.now()) {
      end = Date.now() + this.#duration;
      try {
        sessionStorage.setItem(key, String(end));
      } catch {}
    }
    return end;
  }

  #tick() {
    const left = Math.max(0, this.#deadline() - Date.now());
    const minutes = Math.floor(left / 60000);
    const seconds = Math.floor((left % 60000) / 1000);
    const out = this.querySelector('[data-time]');
    if (out) out.textContent = `${minutes}:${String(seconds).padStart(2, '0')}`;
  }
}

/** Toggle that adds/removes the shipping protection product. */
class RowaShippingProtection extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    this.addEventListener('change', this.#onChange, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  #onChange = async (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const variantId = Number(this.dataset.variantId);
    input.disabled = true;
    try {
      if (input.checked) {
        await cartRequest('cart/add.js', { items: [{ id: variantId, quantity: 1 }] }, this, 'add');
      } else {
        await cartRequest('cart/change.js', { id: String(variantId), quantity: 0 }, this, 'update');
      }
    } catch {
      input.checked = !input.checked;
    } finally {
      input.disabled = false;
    }
  };
}

/**
 * Upsell list in the cart drawer. In "ai" mode the cards come from Shopify's product recommendations
 * (rendered by sections/rowa-cart-upsell.liquid); in "manual" mode they are rendered server-side.
 * Re-fetches when the products in the cart change (data-cart-products is updated on every re-render).
 */
class RowaCartUpsell extends HTMLElement {
  static observedAttributes = ['data-cart-products'];

  #controller = new AbortController();
  #request = 0;

  connectedCallback() {
    this.addEventListener('click', this.#onClick, { signal: this.#controller.signal });
    this.querySelectorAll('[data-scroll]').forEach((button) =>
      button.addEventListener('click', () => this.#scroll(Number(button.getAttribute('data-scroll'))), {
        signal: this.#controller.signal,
      })
    );
    this.#load();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  attributeChangedCallback(name, before, after) {
    if (before !== null && before !== after) this.#load();
  }

  get #track() {
    return this.querySelector('[data-upsell-track]');
  }

  async #load() {
    if (this.dataset.mode !== 'ai' || !this.dataset.productId) return this.#filter();
    const id = ++this.#request;
    const url = new URL(`${root()}recommendations/products`, window.location.origin);
    url.searchParams.set('product_id', this.dataset.productId);
    url.searchParams.set('limit', String(Number(this.dataset.limit || 4) + 4));
    url.searchParams.set('intent', this.dataset.intent || 'related');
    url.searchParams.set('section_id', 'rowa-cart-upsell');
    try {
      const html = await (await fetch(url)).text();
      if (id !== this.#request) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const cards = doc.querySelector('[data-upsell-track]');
      // keep the manual fallback cards when Shopify has no recommendation yet
      if (cards?.querySelector('[data-upsell-card]') && this.#track) this.#track.innerHTML = cards.innerHTML;
    } catch {}
    this.#filter();
  }

  /** Hides products already in the cart (optional) and the extra cards beyond the limit. */
  #filter() {
    const inCart = (this.dataset.cartProducts || '').split(',').filter(Boolean);
    const hide = this.dataset.hideInCart === 'true';
    const limit = Number(this.dataset.limit || 4);
    let shown = 0;
    for (const card of this.querySelectorAll('[data-upsell-card]')) {
      const id = card.getAttribute('data-product-id');
      // the shipping protection product is never offered
      const skip = id === this.dataset.exclude || (hide && inCart.includes(id)) || shown >= limit;
      card.hidden = skip;
      if (!skip) shown += 1;
    }
  }

  /** @param {number} dir */
  #scroll(dir) {
    const track = this.#track;
    const card = track?.querySelector('[data-upsell-card]:not([hidden])');
    if (!(track instanceof HTMLElement) || !(card instanceof HTMLElement)) return;
    track.scrollBy({ left: dir * (card.offsetWidth + 12), behavior: 'smooth' });
  }

  #onClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-upsell-add]') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    const field = button.closest('[data-upsell-card]')?.querySelector('[name="id"]');
    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) return;
    button.disabled = true;
    try {
      await cartRequest('cart/add.js', { items: [{ id: Number(field.value), quantity: 1 }] }, this, 'add');
    } catch {
      button.disabled = false;
    }
  };
}

if (!customElements.get('rowa-cart-timer')) customElements.define('rowa-cart-timer', RowaCartTimer);
if (!customElements.get('rowa-shipping-protection')) customElements.define('rowa-shipping-protection', RowaShippingProtection);
if (!customElements.get('rowa-cart-upsell')) customElements.define('rowa-cart-upsell', RowaCartUpsell);
